// ✅ COMPLETE CLEAN VERSION - Replace your ENTIRE appwriteChat.js with this:

import { Client, Databases, Storage, Account, ID, Query } from "appwrite";
import { appwriteConfig, getCurrentUser } from "./appwrite";

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId);

const databases = new Databases(client);
const account = new Account(client);
const storage = new Storage(client);

// Customer collection ID - hardcoded as you provided
const customerCollectionId = "67f92ae10033c01a8f8c";

// Get user's IP and location information using ipapi
const getUserLocationInfo = async () => {
  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();
    return {
      ip: data.ip,
      country: data.country_code,
      countryName: data.country_name,
      city: data.city,
      language: data.languages?.split(",")[0] || "en",
    };
  } catch (error) {
    console.error("Error fetching location info:", error);
    return {
      ip: "0.0.0.0",
      country: "Unknown",
      countryName: "Unknown",
      city: "Unknown",
      language: "en",
    };
  }
};

// Get browser information
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browserName;

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "Chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "Firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "Safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "Opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "Edge";
  } else {
    browserName = "Unknown";
  }

  return browserName;
};

// Get the current session or create an anonymous session if none exists
const getOrCreateAnonymousSession = async () => {
  try {
    try {
      const session = await account.getSession("current");
      console.log("Using existing session:", session);
      return session;
    } catch (error) {
      console.log("No active session found, creating anonymous session");
      const response = await account.createAnonymousSession();
      console.log("Created anonymous session:", response);
      return response;
    }
  } catch (error) {
    console.error("Error managing session:", error);
    throw error;
  }
};

// Delete the current session
const deleteCurrentSession = async () => {
  try {
    await account.deleteSession("current");
    console.log("Session deleted successfully");
    return true;
  } catch (error) {
    console.error("Error deleting session:", error);
    return false;
  }
};

// Check if a customer exists with the given email
const checkExistingCustomer = async (email) => {
  try {
    const customers = await databases.listDocuments(
      appwriteConfig.databaseId,
      customerCollectionId,
      [Query.equal("email", email)]
    );

    return customers.documents.length > 0 ? customers.documents[0] : null;
  } catch (error) {
    console.error("Error checking existing customer:", error);
    return null;
  }
};

// Store customer info in localStorage
const storeCustomerInfo = (customer) => {
  localStorage.setItem("chat_customer_id", customer.$id);
  localStorage.setItem("chat_customer_email", customer.email);
  localStorage.setItem("chat_customer_name", customer.username);
  localStorage.setItem("chat_customer_anonymous_id", customer.anonymousId);
};

// Get stored customer info from localStorage
const getStoredCustomerInfo = () => {
  const customerId = localStorage.getItem("chat_customer_id");
  const customerEmail = localStorage.getItem("chat_customer_email");
  const customerName = localStorage.getItem("chat_customer_name");
  const anonymousId = localStorage.getItem("chat_customer_anonymous_id");

  if (customerId && customerEmail && customerName && anonymousId) {
    return {
      $id: customerId,
      email: customerEmail,
      username: customerName,
      anonymousId: anonymousId,
    };
  }

  return null;
};

// Create a new customer in the database
const createCustomer = async (formData) => {
  try {
    const existingCustomer = await checkExistingCustomer(formData.email);
    if (existingCustomer) {
      console.log("Customer already exists, returning existing customer");
      storeCustomerInfo(existingCustomer);
      return existingCustomer;
    }

    const currentUser = await getCurrentUser();
    const companyId = currentUser.companyId;

    const locationInfo = await getUserLocationInfo();
    const browser = getBrowserInfo();
    const anonymousId = ID.unique();

    const customerData = {
      companyId: companyId,
      email: formData.email,
      username: formData.name,
      avatar: null,
      ip: locationInfo.ip,
      countryName: locationInfo.countryName,
      status: "active",
      updated_at: new Date().toISOString(),
      anonymousId: anonymousId,
      language: locationInfo.language,
      browser: browser,
      is_anonymous: true,
      country: locationInfo.country,
      city: locationInfo.city,
    };

    console.log("Creating customer with data:", customerData);

    const customer = await databases.createDocument(
      appwriteConfig.databaseId,
      customerCollectionId,
      ID.unique(),
      customerData
    );

    storeCustomerInfo(customer);
    return customer;
  } catch (error) {
    console.error("Error creating customer:", error);
    throw error;
  }
};

// Create a new conversation
const createConversation = async (customerId, customerName) => {
  try {
    const storedConversationId = localStorage.getItem("chat_conversation_id");

    if (storedConversationId) {
      try {
        const existingConversation = await databases.getDocument(
          appwriteConfig.databaseId,
          appwriteConfig.conversationCollectionId,
          storedConversationId
        );

        console.log("Found existing conversation:", existingConversation);
        return existingConversation;
      } catch (error) {
        console.log("Stored conversation not found, creating a new one");
      }
    }

    const currentUser = await getCurrentUser();
    const companyId = currentUser.companyId;

    console.log("Using dynamic company ID for conversation:", companyId);

    const conversationId = ID.unique();

    const conversationData = {
      title: `Chat with ${customerName}`,
      participants: [customerId],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_message_preview: `Chat started by ${customerName}`,
      companyId: companyId,
      channel: "messenger",
      status: "inbox",
      unreadCount: 0,
      country: "CH",
      phone_number: null,
      messageStatus: "sent",
      rating: null,
      topic: "general",
      isNewConversation: true,
      lastReadBy: [],
      last_sender_id: customerId,
    };

    console.log("Creating conversation with data:", conversationData);

    const conversation = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationId,
      conversationData
    );

    localStorage.setItem("chat_conversation_id", conversation.$id);

    return conversation;
  } catch (error) {
    console.error("Error creating conversation:", error);
    throw error;
  }
};

// Create initial message in the conversation
const createInitialMessage = async (conversationId, customerId, content) => {
  try {
    const messageData = {
      conversation_id: conversationId,
      sender_id: customerId,
      sender_type: "customer",
      content: content || "Hello, I would like to chat with you.",
      created_at: new Date().toISOString(),
      status: "sent",
      is_read: false,
    };

    const message = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.messageCollectionId,
      ID.unique(),
      messageData
    );

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationId,
      {
        last_sender_id: customerId,
        last_message_preview:
          content || "Hello, I would like to chat with you.",
        updated_at: new Date().toISOString(),
      }
    );

    return message;
  } catch (error) {
    console.error("Error creating initial message:", error);
    throw error;
  }
};

// Fetch messages for a specific conversation
const fetchMessages = async (conversationId) => {
  try {
    console.log("Fetching messages for conversation:", conversationId);

    const messages = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.messageCollectionId,
      [
        Query.equal("conversation_id", conversationId),
        Query.orderAsc("created_at"),
      ]
    );

    console.log(`Fetched ${messages.documents.length} messages`);
    return messages.documents;
  } catch (error) {
    console.error("Error fetching messages:", error);
    return [];
  }
};

// Send message
const sendMessage = async (
  conversationId,
  senderId,
  content,
  senderType = "customer",
  imageData = null
) => {
  try {
    console.log(
      `Sending ${senderType} message to conversation ${conversationId}`
    );

    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const messageData = {
      conversation_id: conversationIdString,
      sender_id: senderId,
      sender_type: senderType,
      content: content || (imageData ? "📷 Image" : ""),
      created_at: new Date().toISOString(),
      status: "sent",
      is_read: false,
      channel: "messenger",
    };

    if (imageData) {
      messageData.message_type = "image";
      messageData.image_url = imageData.fileUrl;
      messageData.image_file_id = imageData.fileId;
      messageData.file_name = imageData.originalName;
      messageData.file_size = imageData.fileSize;
      messageData.mime_type = imageData.mimeType;
      messageData.download_url = imageData.downloadUrl;
    } else {
      messageData.message_type = "text";
    }

    console.log("Creating message with data:", messageData);

    const message = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.messageCollectionId,
      ID.unique(),
      messageData
    );

    const previewText = imageData ? "📷 Image" : content;
    const truncatedPreview =
      previewText.length > 100
        ? previewText.substring(0, 97) + "..."
        : previewText;

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        last_message_preview: truncatedPreview,
        updated_at: new Date().toISOString(),
        unreadCount: 1,
        isNewConversation: false,
        lastReadBy: [],
        last_sender_id: senderId,
      }
    );

    console.log("✅ Message sent successfully:", message);
    return message;
  } catch (error) {
    console.error("❌ Error sending message:", error);
    throw error;
  }
};

// Clear chat session data from localStorage
const clearChatSession = () => {
  localStorage.removeItem("chat_customer_id");
  localStorage.removeItem("chat_customer_email");
  localStorage.removeItem("chat_customer_name");
  localStorage.removeItem("chat_customer_anonymous_id");
  localStorage.removeItem("chat_conversation_id");
};

// Check if customer has an active chat session
const checkActiveChat = async () => {
  const storedCustomer = getStoredCustomerInfo();
  const conversationId = localStorage.getItem("chat_conversation_id");

  if (!storedCustomer || !conversationId) {
    console.log("No stored customer info or conversation ID found");
    return { isActive: false };
  }

  try {
    let conversation;
    try {
      console.log(`Checking if conversation ${conversationId} exists...`);
      conversation = await databases.getDocument(
        appwriteConfig.databaseId,
        appwriteConfig.conversationCollectionId,
        conversationId
      );
      console.log("Conversation found:", conversation);
    } catch (error) {
      console.log(`Conversation ${conversationId} not found: ${error.message}`);
      clearChatSession();
      return { isActive: false };
    }

    let messages = [];
    try {
      messages = await fetchMessages(conversationId);
    } catch (error) {
      console.log(`Error fetching messages: ${error.message}`);
    }

    return {
      isActive: true,
      customer: storedCustomer,
      conversation: conversation,
      messages: messages,
    };
  } catch (error) {
    console.log("Error checking active chat:", error);
    return { isActive: false };
  }
};

// Main function to handle the start of a chat
const handleChatStart = async (formData) => {
  try {
    console.log("Starting chat with form data:", formData);

    const activeChat = await checkActiveChat();

    if (activeChat.isActive) {
      console.log("Active chat session found:", activeChat);
      return {
        success: true,
        customer: activeChat.customer,
        conversation: activeChat.conversation,
        messages: activeChat.messages,
      };
    }

    const session = await getOrCreateAnonymousSession();
    const customer = await createCustomer(formData);
    const conversation = await createConversation(
      customer.anonymousId,
      formData.name
    );

    const initialMessage = await createInitialMessage(
      conversation.$id,
      customer.anonymousId,
      `Hello, this is ${formData.name}. I would like to chat with you.`
    );

    const messages = await fetchMessages(conversation.$id);

    return {
      success: true,
      customer: customer,
      conversation: conversation,
      messages: messages,
      session: session,
    };
  } catch (error) {
    console.error("Error starting chat:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Function to subscribe to conversation changes using client.subscribe
const subscribeToConversation = (conversationId, onUpdate) => {
  if (!conversationId) {
    console.log("Cannot subscribe: missing conversation ID");
    return () => {};
  }

  try {
    const channelId = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.conversationCollectionId}.documents.${conversationId}`;

    const unsubscribe = client.subscribe(channelId, (response) => {
      console.log("Realtime update received:", response);

      if (
        response.events.includes(
          `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.conversationCollectionId}.documents.${conversationId}.update`
        )
      ) {
        onUpdate(response.payload);
      }
    });

    console.log(`Subscribed to conversation updates for: ${conversationId}`);
    return unsubscribe;
  } catch (error) {
    console.error("Error subscribing to conversation:", error);
    return () => console.log("No subscription to unsubscribe from");
  }
};

// ✅ SINGLE handleAgentJoinedChat function (NO DUPLICATES)
const handleAgentJoinedChat = async (conversation, customer) => {
  try {
    if (conversation.participants && conversation.participants.length > 1) {
      const agentIds = conversation.participants.filter(
        (id) => id !== customer.anonymousId
      );

      if (agentIds.length > 0) {
        const agentId = agentIds[agentIds.length - 1];
        console.log("Found new participant ID:", agentId);

        const agentInfo = await fetchAgentDetails(agentId);

        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.conversationCollectionId,
          conversation.$id,
          {
            agentName: agentInfo.agentName || "Support Agent",
            agentProfile: agentInfo.avatar || null,
            status: "active",
            updated_at: new Date().toISOString(),
          }
        );

        console.log("✅ Conversation updated with agent details:", agentInfo);

        return {
          agentInfo,
          conversationUpdated: true,
        };
      }
    }

    return null;
  } catch (error) {
    console.error("Error handling agent joined event:", error);
    return null;
  }
};

// ✅ SINGLE fetchAgentDetails function (NO DUPLICATES)
async function fetchAgentDetails(agentId) {
  try {
    console.log("🔍 Fetching agent details for:", agentId);

    const collectionsToSearch = [
      "67e535c100387bd4b7e8", // companyCollection
      "67e535d7003e35a9938d", // userCollection
    ];

    for (const collectionId of collectionsToSearch) {
      try {
        console.log(`Searching for agent in collection: ${collectionId}`);

        const result = await databases.getDocument(
          appwriteConfig.databaseId,
          collectionId,
          agentId
        );

        if (result) {
          console.log("✅ Found agent document:", result);

          return {
            agentName:
              result.username ||
              result.name ||
              result.companyName ||
              "Support Agent",
            avatar: result.avatar || null,
            agentId: agentId,
            email: result.email || null,
          };
        }
      } catch (error) {
        console.log(
          `Agent not found in collection ${collectionId}:`,
          error.message
        );
      }
    }

    console.log("⚠️ Agent not found, using fallback details");
    return {
      agentName: "Support Agent",
      avatar: null,
      agentId: agentId,
    };
  } catch (error) {
    console.error("❌ Error fetching agent details:", error);
    return {
      agentName: "Support Agent",
      avatar: null,
      agentId: agentId,
    };
  }
}

// Escalate to human
export const escalateToHuman = async (
  conversationId,
  escalationReason = "Customer request"
) => {
  try {
    console.log("🚨 Escalating conversation to human agent:", conversationId);

    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const updatedConversation = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        aiChat: false,
        aiResolved: false,
        status: "inbox",
        updated_at: new Date().toISOString(),
        escalation_reason: escalationReason,
      }
    );

    console.log("✅ Conversation escalated successfully:", updatedConversation);
    return updatedConversation;
  } catch (error) {
    console.error("❌ Error escalating conversation:", error);
    throw error;
  }
};

// Add this function to appwriteChat.js
export const markChatEnded = async (conversationId) => {
  try {
    console.log("🏁 Marking conversation as ended:", conversationId);

    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const updatedConversation = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        aiChat: true,
        aiResolved: true,
        conversationStatus: "ended",
        status: "resolved",
        updated_at: new Date().toISOString(),
      }
    );

    console.log("✅ Conversation marked as ended:", updatedConversation);
    return updatedConversation;
  } catch (error) {
    console.error("❌ Error marking conversation as ended:", error);
    throw error;
  }
};

// Mark AI resolved
export const markAIResolved = async (conversationId) => {
  try {
    console.log("✅ Marking conversation as AI resolved:", conversationId);

    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const updatedConversation = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        aiChat: true,
        aiResolved: true,
        status: "resolved",
        updated_at: new Date().toISOString(),
      }
    );

    console.log("✅ Conversation marked as AI resolved:", updatedConversation);
    return updatedConversation;
  } catch (error) {
    console.error("❌ Error marking conversation as resolved:", error);
    throw error;
  }
};

// Update conversation status (for dashboard/admin use)
export const updateConversationStatus = async (conversationId, newStatus) => {
  try {
    console.log(
      `🔄 Updating conversation ${conversationId} status to: ${newStatus}`
    );

    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const updatedConversation = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        conversationStatus: newStatus,
        updated_at: new Date().toISOString(),
      }
    );

    console.log("✅ Conversation status updated successfully");
    return updatedConversation;
  } catch (error) {
    console.error("❌ Error updating conversation status:", error);
    throw error;
  }
};

// Get current conversation status
export const getConversationStatus = async (conversationId) => {
  try {
    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const conversation = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString
    );

    return conversation.conversationStatus || "active";
  } catch (error) {
    console.error("❌ Error getting conversation status:", error);
    return "active"; // Default fallback
  }
};

// ✅ NEW: Create AI Analytics Document
export const createAIAnalytics = async (analyticsData) => {
  try {
    console.log("📊 Creating AI analytics document:", analyticsData);

    const document = await databases.createDocument(
      appwriteConfig.aiDatabaseId,
      appwriteConfig.aiAnalyticsCollectionId,
      ID.unique(),
      {
        conversationId: analyticsData.conversationId,
        companyId: analyticsData.companyId,
        userId: analyticsData.userId,
        question: analyticsData.question,
        response: analyticsData.response || "",
        confidence: analyticsData.confidence || "medium",
        usedDocuments: analyticsData.usedDocuments || false,
        documentsFound: analyticsData.documentsFound || 0,
        responseTime: analyticsData.responseTime || 0,
        escalatedToHuman: analyticsData.escalatedToHuman || false,
        escalationReason: analyticsData.escalationReason || "",
        customerSatisfaction: analyticsData.customerSatisfaction || 0,
        timestamp: analyticsData.timestamp || new Date().toISOString(),
        language: analyticsData.language || "de",
        questionType: analyticsData.questionType || "general",
        responseLength: analyticsData.responseLength || 0,
        errorOccurred: analyticsData.errorOccurred || false,
        errorMessage: analyticsData.errorMessage || "",
        feedbackComment: analyticsData.feedbackComment || "",
        feedbackSubmittedAt: analyticsData.feedbackSubmittedAt || null,
      }
    );

    console.log("✅ AI analytics document created:", document.$id);
    return document;
  } catch (error) {
    console.error("❌ Error creating AI analytics document:", error);
    throw error;
  }
};

// ✅ NEW: Update AI Analytics Document
export const updateAIAnalytics = async (documentId, updateData) => {
  try {
    console.log("📊 Updating AI analytics document:", documentId, updateData);

    // Filter out undefined values
    const cleanUpdateData = Object.fromEntries(
      Object.entries(updateData).filter(([_, value]) => value !== undefined)
    );

    const document = await databases.updateDocument(
      appwriteConfig.aiDatabaseId,
      appwriteConfig.aiAnalyticsCollectionId,
      documentId,
      cleanUpdateData
    );

    console.log("✅ AI analytics document updated:", document.$id);
    return document;
  } catch (error) {
    console.error("❌ Error updating AI analytics document:", error);
    throw error;
  }
};

export const uploadConversationFile = async (file) => {
  try {
    const fileId = ID.unique();

    console.log("📎 Uploading file to conversation bucket:", file.name);

    // Upload to your conversationImages bucket (same as Dashboard)
    const uploadedFile = await storage.createFile(
      "6864e70f0014aeb0920f", // Your conversationImages bucket ID
      fileId,
      file
    );

    if (!uploadedFile) throw new Error("File upload failed");

    // Generate file URLs (same format as Dashboard)
    const fileUrl = `${appwriteConfig.endpoint}/storage/buckets/6864e70f0014aeb0920f/files/${uploadedFile.$id}/view?project=${appwriteConfig.projectId}&mode=admin`;
    const downloadUrl = `${appwriteConfig.endpoint}/storage/buckets/6864e70f0014aeb0920f/files/${uploadedFile.$id}/download?project=${appwriteConfig.projectId}&mode=admin`;

    console.log("✅ File uploaded successfully:", {
      fileId: uploadedFile.$id,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    return {
      fileId: uploadedFile.$id,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      fileUrl: fileUrl,
      downloadUrl: downloadUrl,
      originalName: file.name,
    };
  } catch (error) {
    console.error("❌ Error uploading conversation file:", error);
    throw new Error(`File upload failed: ${error.message}`);
  }
};

// 🆕 NEW: Enhanced sendMessage with file support
export const sendMessageWithFiles = async (
  conversationId,
  senderId,
  content,
  senderType = "customer",
  files = []
) => {
  try {
    console.log("📨 Sending message with files:", {
      content,
      filesCount: files.length,
    });

    // Process files first if any
    let fileData = null;
    if (files.length > 0) {
      console.log("📎 Processing files...");

      // For now, handle single file (you can extend for multiple files later)
      const file = files[0];
      const uploadResult = await uploadConversationFile(file);

      fileData = {
        image_url: uploadResult.fileUrl,
        image_file_id: uploadResult.fileId,
        file_name: uploadResult.fileName,
        file_size: uploadResult.fileSize,
        mime_type: uploadResult.mimeType,
        download_url: uploadResult.downloadUrl,
      };
    }

    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const messageData = {
      conversation_id: conversationIdString,
      sender_id: senderId,
      sender_type: senderType,
      content: content || (fileData ? "📷 Image" : ""), // Allow empty content if there's a file
      created_at: new Date().toISOString(),
      status: "sent",
      is_read: false,
      channel: "messenger",

      // Add file data if present
      ...fileData,
    };

    if (fileData) {
      messageData.message_type = "image";
    } else {
      messageData.message_type = "text";
    }

    console.log("💾 Creating message with data:", messageData);

    // Create message document
    const message = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.messageCollectionId,
      ID.unique(),
      messageData
    );

    // Update conversation
    const previewText =
      content || (fileData ? `📎 ${fileData.file_name}` : "File shared");
    const truncatedPreview =
      previewText.length > 100
        ? previewText.substring(0, 97) + "..."
        : previewText;

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        last_message_preview: truncatedPreview,
        updated_at: new Date().toISOString(),
        unreadCount: 1,
        isNewConversation: false,
        lastReadBy: [],
        last_sender_id: senderId,
      }
    );

    console.log("✅ Message sent successfully with files");
    return message;
  } catch (error) {
    console.error("❌ Error sending message with files:", error);
    throw error;
  }
};

// 🆕 NEW: Check if file is an image
export const isImageFile = (mimeType) => {
  return mimeType && mimeType.startsWith("image/");
};

// 🆕 NEW: Format file size for display
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Export all functions (SINGLE EXPORT OF EACH)
export {
  getOrCreateAnonymousSession,
  deleteCurrentSession,
  createCustomer,
  createConversation,
  createInitialMessage,
  handleChatStart,
  sendMessage,
  fetchMessages,
  checkActiveChat,
  clearChatSession,
  subscribeToConversation,
  handleAgentJoinedChat,
};
